import { ReactNode } from 'react'
import { useDevFlags } from '@/contexts/DevFlagsContext'

interface DebugModeOnlyProps {
  children: ReactNode
}

/**
 * Conditional wrapper for debug-mode-only content
 *
 * Shows children only when debug mode is enabled (toggled via DevTools menu).
 * In normal mode (debug off), renders nothing (not even a DOM element).
 *
 * Use this for inline debug information, visualization tools, and diagnostic UI
 * that should only appear when the user has explicitly enabled debug mode.
 *
 * Note: Unlike DevelopmentOnly, this is controlled by a runtime toggle that
 * users can enable/disable via the DevTools menu (Shift+D).
 *
 * @example
 * <DebugModeOnly>
 *   <div className="debug-info">
 *     Container: {width}x{height}
 *   </div>
 * </DebugModeOnly>
 *
 * @example
 * <DebugModeOnly>
 *   <div className="absolute top-2 left-2 bg-black/70 text-white text-xs p-2">
 *     Debug: {someValue}
 *   </div>
 * </DebugModeOnly>
 */
export function DebugModeOnly({ children }: DebugModeOnlyProps) {
  const devFlags = useDevFlags()
  const debugMode = devFlags?.flags.debugUI ?? false

  // Only render children when debug mode is enabled
  if (!debugMode) {
    return null
  }

  return <>{children}</>
}
